import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Search, Plus, ArrowLeft, Eye, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const AREAS = [
  'Gerencia de Fortalecimiento Territorial',
  'Gerencia de Emprendimiento',
  'Gerencia de Innovación',
  'Gerencia de Competitividad',
  'Dirección General'
];

export default function MisConvocatorias() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [convocatorias, setConvocatorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState({
    codigo: '',
    nombre: '',
    area: ''
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, convocatoria: null });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConvocatorias();
  }, []);

  const fetchConvocatorias = async (params = {}) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (params.codigo || searchParams.codigo) queryParams.append('codigo', params.codigo || searchParams.codigo);
      if (params.nombre || searchParams.nombre) queryParams.append('nombre', params.nombre || searchParams.nombre);
      if (params.area || searchParams.area) queryParams.append('area', params.area || searchParams.area);

      const response = await fetch(`${API_URL}/api/convocatorias?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar convocatorias');
      }

      const data = await response.json();
      setConvocatorias(data);
    } catch (error) {
      toast.error('Error al cargar las convocatorias');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchConvocatorias(searchParams);
  };

  const handleDelete = async () => {
    if (!deleteModal.convocatoria) return;
    
    try {
      const response = await fetch(`${API_URL}/api/convocatorias/${deleteModal.convocatoria.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar');
      }

      toast.success('Convocatoria eliminada exitosamente');
      setDeleteModal({ open: false, convocatoria: null });
      fetchConvocatorias();
    } catch (error) {
      toast.error('Error al eliminar la convocatoria');
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Activa':
        return 'bg-green-100 text-green-800';
      case 'Cerrada':
        return 'bg-red-100 text-red-800';
      case 'Próxima':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">
                Mis Convocatorias
              </h1>
              <p className="text-sm text-slate-300">
                {user?.first_name} {user?.first_lastname}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Volver al Panel</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title and Register Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-slate-900">MIS CONVOCATORIAS</h2>
          <button
            onClick={() => navigate('/convocatorias/nueva')}
            className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <Plus className="h-5 w-5" />
            REGISTRAR
          </button>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Código:</label>
              <input
                type="text"
                value={searchParams.codigo}
                onChange={(e) => setSearchParams(prev => ({ ...prev, codigo: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Buscar por código"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nombre:</label>
              <input
                type="text"
                value={searchParams.nombre}
                onChange={(e) => setSearchParams(prev => ({ ...prev, nombre: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Buscar por nombre"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Área:</label>
              <select
                value={searchParams.area}
                onChange={(e) => setSearchParams(prev => ({ ...prev, area: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccione...</option>
                {AREAS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <Search className="h-5 w-5" />
                BUSCAR
              </button>
            </div>
          </form>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Cargando...</div>
          ) : convocatorias.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No hay convocatorias registradas
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Código</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nombre</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 hidden md:table-cell">Descripción</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 hidden lg:table-cell">Fecha Inicio</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 hidden lg:table-cell">Fecha Fin</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 hidden xl:table-cell">Área</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Estado</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {convocatorias.map((conv) => (
                    <tr key={conv.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-900 font-medium">{conv.codigo}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{conv.nombre}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden md:table-cell">
                        <div className="max-w-xs truncate">{conv.descripcion}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden lg:table-cell">{conv.fecha_inicio}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden lg:table-cell">{conv.fecha_final}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden xl:table-cell">{conv.area || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoColor(conv.estado)}`}>
                          {conv.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/convocatorias/${conv.id}`)}
                            className="p-1 text-slate-500 hover:text-blue-600 transition-colors"
                            title="Ver detalle"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => navigate(`/convocatorias/${conv.id}/editar`)}
                            className="p-1 text-slate-500 hover:text-yellow-600 transition-colors"
                            title="Editar"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setDeleteModal({ open: true, convocatoria: conv })}
                            className="p-1 text-slate-500 hover:text-red-600 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Confirmar eliminación</h3>
              <button
                onClick={() => setDeleteModal({ open: false, convocatoria: null })}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-slate-600 mb-6">
              ¿Está seguro que desea eliminar la convocatoria <strong>"{deleteModal.convocatoria?.nombre}"</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, convocatoria: null })}
                className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
